import { Dayjs } from 'dayjs';
import moment from 'moment';

import { ShipmentObj } from '@Reducers/Invoice/Type';

import { SHIPMENT_STATUS } from '../../../../constants/Shipment';
import { formatNumber } from '../../../../utils/formatNumber';
import { AccountHolderUnbilledData } from '../../Type';

export type CreditDebitNoteType = 'credit' | 'debit';

export interface SelectedCreditDebitParamType {
  accountNumber: string;
  _systemId: string;
  systemName: string;
  shipments: ShipmentObj[];
  totalAmount: number;
  invoiceNumber: number | string;
  _userId?: string;
  uniqueKey?: string;
}

export interface CreditDebitConfig {
  invoiceDate: Dayjs | null;
  dueDate: Dayjs | null;
  remarks: string;
}

export interface CreditDebitFilterType {
  currentPage: number;
  perPage: number;
  fromDate: string;
  toDate: string;
  systems: string[];
  searchKey?: string;
  searchValue?: string;
}

export const initialCreditDebitFilterDateRange = {
  startDate: moment().subtract(1, 'months').startOf('day').toDate(),
  endDate: moment().endOf('day').toDate(),
};

export const initialCreditDebitConfig: CreditDebitConfig = {
  invoiceDate: null,
  dueDate: null,
  remarks: '',
};

export const CreditDebitSearchFilterList = [
  {
    label: 'Account No.',
    value: 'account_no',
  },
  {
    label: 'Shipment No.',
    value: 'shipment_no',
  },
  {
    label: 'Invoice Number',
    value: 'invoice_no',
  },
];

export const NonAccountCreditDebitSearchFilterList = [
  {
    label: 'Shipment No.',
    value: 'shipment_no',
  },
  {
    label: 'Invoice Number',
    value: 'invoice_no',
  },
];

export function getCreditDebitGroupKey(item: {
  accountNumber?: string;
  account?: { accountNumber?: string };
  invoiceNumber?: number | string;
  _systemId?: { _id?: string; name?: string } | string;
  system?: { _id?: string; name?: string } | string;
  uniqueKey?: string;
}): string {
  if (item.uniqueKey) return item.uniqueKey;
  const invNo = item.invoiceNumber !== undefined && item.invoiceNumber !== null ? String(item.invoiceNumber) : '';
  const accNo = item.account?.accountNumber || item.accountNumber || '';
  const sysId =
    typeof item._systemId === 'string'
      ? item._systemId
      : item._systemId?._id ||
        (typeof item.system === 'object' && item.system !== null
          ? item.system._id
          : typeof item.system === 'string'
          ? item.system
          : '') ||
        '';
  if (invNo) {
    return `${accNo}_${invNo}_${sysId}`;
  }
  return `${accNo}_${sysId}`;
}

export function isSameCreditDebitEntry(
  a: SelectedCreditDebitParamType,
  b: SelectedCreditDebitParamType | AccountHolderUnbilledData,
): boolean {
  if (a.uniqueKey && b.uniqueKey) {
    return a.uniqueKey === b.uniqueKey;
  }
  const aInv = a.invoiceNumber !== undefined && a.invoiceNumber !== null ? String(a.invoiceNumber) : '';
  const bInv = b.invoiceNumber !== undefined && b.invoiceNumber !== null ? String(b.invoiceNumber) : '';
  if (aInv && bInv) {
    return aInv === bInv;
  }
  const aAcc = a.accountNumber || '';
  const bAcc = ('account' in b && b.account?.accountNumber ? b.account.accountNumber : b.accountNumber) || '';
  const aSysId = typeof a._systemId === 'string' ? a._systemId : '';
  const bSys = 'system' in b ? b.system : undefined;
  const bSysId =
    typeof b._systemId === 'string'
      ? b._systemId
      : b._systemId?._id ||
        (typeof bSys === 'object' && bSys !== null ? bSys._id : typeof bSys === 'string' ? bSys : '') ||
        '';
  return Boolean(aAcc && bAcc && aAcc === bAcc) && Boolean(aSysId && bSysId && aSysId === bSysId);
}

export function getShipmentExtraChargeSum(s: ShipmentObj, type: CreditDebitNoteType): number {
  const extraChargesNotes = s?.invoiceCharges;
  if (!extraChargesNotes) return 0;
  const additionKey = type === 'debit' ? 'add' : 'discount';

  const sellingCharges =
    extraChargesNotes.selling && extraChargesNotes.selling[additionKey]
      ? Object.values(extraChargesNotes.selling[additionKey]).reduce((acc, section) => {
          return section ? acc + (section.value || 0) : acc;
        }, 0)
      : 0;

  const pricingCharges =
    extraChargesNotes.pricing && extraChargesNotes.pricing[additionKey]
      ? Object.values(extraChargesNotes.pricing[additionKey]).reduce((acc, section) => {
          if (!section) return acc;
          const subSum = Object.values(section).reduce((acc2, val) => {
            return val ? acc2 + Math.abs(val) : acc2;
          }, 0);
          return acc + subSum;
        }, 0)
      : 0;

  return sellingCharges + pricingCharges;
}

export function calculateSelectedCreditDebitPricingSum(
  selectedRows: SelectedCreditDebitParamType[],
  noteType: CreditDebitNoteType,
): number {
  if (!selectedRows?.length) return 0;
  return selectedRows.reduce((sum, entry) => {
    const entryShipments = entry?.shipments || [];
    const total = entryShipments.reduce((acc, shipment) => acc + getShipmentExtraChargeSum(shipment, noteType), 0);
    return sum + total;
  }, 0);
}

export function updateSelectedCreditDebitRowsList(
  prevRows: SelectedCreditDebitParamType[],
  params: SelectedCreditDebitParamType,
): SelectedCreditDebitParamType[] {
  const filtered = prevRows.filter(item => !isSameCreditDebitEntry(item, params));
  if (params.shipments?.length) {
    filtered.push(params);
  }
  return filtered;
}

export function validateCreditDebitConfig(
  config: CreditDebitConfig,
  accountRows: SelectedCreditDebitParamType[],
  noteType: CreditDebitNoteType,
): string[] {
  const missingFields: string[] = [];
  const { invoiceDate, dueDate } = config;

  if (!invoiceDate) {
    missingFields.push('Invoice Date');
  }

  if (noteType === 'debit' && !dueDate) {
    missingFields.push('Due Date');
  }

  const totalShipments = accountRows?.reduce((acc, r) => acc + (r?.shipments?.length || 0), 0) || 0;
  if (!accountRows || accountRows.length === 0 || totalShipments === 0) {
    missingFields.push('At least one shipment');
  }

  return missingFields;
}

export function buildCreditDebitSubmissionPayload(
  config: CreditDebitConfig,
  accountRows: SelectedCreditDebitParamType[],
  noteType: CreditDebitNoteType,
) {
  const formattedInvoiceDate = config.invoiceDate ? config.invoiceDate.format('YYYY-MM-DD') : '';
  const formattedDueDate =
    noteType === 'debit'
      ? config.dueDate
        ? config.dueDate.format('YYYY-MM-DD')
        : formattedInvoiceDate
      : formattedInvoiceDate;

  const invoice = accountRows.map(item => ({
    invoiceNo: String(item.invoiceNumber),
    shipmentIds: item.shipments.map(s => s._id),
    shipmentNumbers: item.shipments.map(s => s.shipmentNumber),
  }));

  return {
    invoice,
    invoiceDate: formattedInvoiceDate,
    dueDate: formattedDueDate,
    remarks: config.remarks || '',
  };
}

export function buildCreditDebitD365Payload(
  config: CreditDebitConfig,
  accountRows: SelectedCreditDebitParamType[],
  noteType: CreditDebitNoteType,
) {
  const formattedInvoiceDate = config.invoiceDate ? config.invoiceDate.format('YYYY-MM-DD') : '';
  const formattedDueDate =
    noteType === 'debit'
      ? config.dueDate
        ? config.dueDate.format('YYYY-MM-DD')
        : formattedInvoiceDate
      : formattedInvoiceDate;

  const accountData = accountRows.map(item => ({
    accountNo: item.accountNumber,
    _systemId: item._systemId,
    _shipmentIds: item.shipments.map(s => s._id),
    _shipmentNumbers: item.shipments.map(s => s.shipmentNumber),
    _userId: item._userId,
  }));

  return {
    accountData,
    invoiceDate: formattedInvoiceDate,
    dueDate: formattedDueDate,
    remarks: config.remarks || '',
  };
}

export interface MappedCreditDebitShipmentRow {
  id: number;
  _id: string;
  date: string;
  shipmentNo: string;
  status: string;
  shipper?: { name?: string; city?: string; country?: string; pincode?: string };
  consignee?: { name?: string; city?: string; country?: string; pincode?: string };
  weight: string;
  packages: number;
  totalAmount: string;
  rawAmount: number;
  isDisabled: boolean;
  serviceName: string;
  awb: string;
}

export function mapCreditDebitShipmentsToGridRows(
  shipments: ShipmentObj[],
  noteType: CreditDebitNoteType,
  locale: string,
): MappedCreditDebitShipmentRow[] {
  return (shipments || []).map((s, i: number) => {
    const rawAmt = getShipmentExtraChargeSum(s, noteType);
    return {
      id: i + 1,
      _id: s._id,
      date: moment(s.createdAt).format('DD/MM/YYYY'),
      shipmentNo: s.shipmentNumber,
      status: SHIPMENT_STATUS[s.status]?.label || s.status || '-',
      shipper: s._shipperId,
      consignee: s._consigneeId,
      weight: s?.sellingPricing?.displayChargeableWeight ? `${s.sellingPricing.displayChargeableWeight}kg` : '-',
      packages: s?._packageId?.box?.length || 0,
      totalAmount: formatNumber(rawAmt, locale),
      rawAmount: rawAmt,
      isDisabled: Boolean(s.carrierDetails && s.carrierDetails?.awb),
      serviceName: s?.sellingPricing?.serviceName || '-',
      awb: s?.carrierDetails?.awb || '-',
    };
  });
}
